<template>
  <nav 
    class="bottom-nav d-lg-none enhanced-bottom-nav" 
    role="navigation" 
    aria-label="Navegación inferior móvil"
    v-if="isUserLoggedIn && quickAccessItems.length > 0"
  >
    <div class="bottom-nav-container">
      <!-- Items de navegación rápida -->
      <div 
        v-for="item in quickAccessItems" 
        :key="item.id"
        class="bottom-nav-item"
        :class="{ 'active': isActiveRoute(item.route) }"
      >
        <router-link 
          :to="item.route" 
          class="bottom-nav-link enhanced-nav-link"
          :aria-label="`Ir a ${item.name}`"
          :aria-current="isActiveRoute(item.route) ? 'page' : null"
          @click="trackNavigation(item)"
          role="tab"
          :tabindex="isActiveRoute(item.route) ? 0 : -1"
        >
          <div class="nav-icon-container">
            <i :class="item.icon" class="nav-icon"></i>
            <div v-if="item.badge" class="nav-badge">
              {{ item.badge }}
            </div>
            <div v-if="isActiveRoute(item.route)" class="active-indicator" aria-hidden="true"></div>
            <!-- Ripple effect container -->
            <div class="ripple-container" aria-hidden="true"></div>
          </div>
          <span class="nav-label">{{ item.name }}</span>
        </router-link>
      </div>
      
      <!-- Botón de más opciones -->
      <div class="bottom-nav-item more-options">
        <button 
          class="bottom-nav-link enhanced-nav-link"
          @click="toggleMoreMenu"
          :aria-expanded="showMoreMenu"
          aria-label="Más opciones"
          :aria-controls="showMoreMenu ? 'more-menu' : null"
          role="button"
          type="button"
        >
          <div class="nav-icon-container">
            <i class="ri-more-line nav-icon" :class="{ 'rotate': showMoreMenu }"></i>
          </div>
          <span class="nav-label">Más</span>
        </button>
        
        <!-- Menú desplegable para más opciones -->
        <div 
          v-if="showMoreMenu" 
          class="more-menu enhanced-more-menu"
          role="menu"
          id="more-menu"
          aria-labelledby="more-menu-header"
          @click.stop
        >
          <div class="more-menu-header">
            <h6 id="more-menu-header">Todos los módulos</h6>
            <button 
              @click="showMoreMenu = false" 
              aria-label="Cerrar menú"
              type="button"
              class="close-menu-btn"
            >
              <i class="ri-close-line" aria-hidden="true"></i>
            </button>
          </div>
          
          <div class="more-menu-grid">
            <router-link 
              v-for="module in allAvailableModules" 
              :key="module.id"
              :to="module.route"
              class="more-menu-item enhanced-menu-item"
              @click="closeMoreMenu"
              role="menuitem"
              :tabindex="showMoreMenu ? 0 : -1"
              :aria-describedby="`module-desc-${module.id}`"
            >
              <div class="more-item-icon">
                <i :class="module.icon"></i>
              </div>
              <span class="more-item-label">{{ module.name }}</span>
              <span :id="`module-desc-${module.id}`" class="sr-only">Ir a la sección {{ module.name }}</span>
            </router-link>
          </div>
          
          <!-- Acceso al perfil de usuario desde el menú más -->
          <div class="more-menu-user">
            <div class="user-info-bottom">
              <div class="user-avatar-bottom">
                <img
                  v-if="user.avatar"
                  :src="user.avatar"
                  :alt="user.name"
                  class="avatar-img"
                />
                <div v-else class="avatar-initials">
                  {{ userInitials }}
                </div>
              </div>
              <div class="user-details-bottom">
                <span class="user-name">{{ user.name }}</span>
                <span class="user-role">{{ user.role || 'Usuario' }}</span>
              </div>
              <button 
                @click="logoutUser" 
                class="logout-btn-bottom"
                title="Cerrar Sesión"
              >
                <i class="ri-logout-box-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Overlay para cerrar el menú más -->
    <div 
      v-if="showMoreMenu" 
      class="more-menu-overlay"
      @click="closeMoreMenu"
    ></div>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'
import { AVAILABLE_MODULES } from '@/constants/modules'

export default {
  name: 'BottomNav',
  
  data() {
    return {
      showMoreMenu: false,
      // Configuración de elementos de acceso rápido
      quickAccessConfig: [
        {
          id: 'bulk_update',
          name: 'Actualización',
          icon: 'ri-upload-2-line',
          route: { name: 'ActualizacionMasiva' },
          permission: 'bulk_update',
          priority: 2,
          badge: null // Se puede agregar badge dinámico
        },
        {
          id: 'activity',
          name: 'Actividad',
          icon: 'ri-line-chart-line',
          route: { name: 'Activity' },
          permission: 'activity',
          priority: 4
        }
      ]
    }
  },
  
  computed: {
    ...mapGetters({
      isUserLoggedIn: 'auth/loggedIn',
      user: 'auth/user'
    }),
    
    // Iniciales del usuario
    userInitials() {
      if (!this.user.name) return 'U';
      
      const names = this.user.name.split(' ');
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    },
    
    // Items de acceso rápido basados en permisos
    quickAccessItems() {
      return this.quickAccessConfig
        .filter(item => this.hasPermission(item.permission))
        .sort((a, b) => a.priority - b.priority)
        .slice(0, 4) // Máximo 4 items + botón "más"
    },
    
    // Todos los módulos disponibles para el menú "más" (desde constante centralizada)
    allAvailableModules() {
      return AVAILABLE_MODULES
        .map(module => ({ ...module, permission: module.id }))
        .filter(module => this.hasPermission(module.permission))
        .filter(module => !this.quickAccessItems.find(quick => quick.id === module.id))
    }
  },
  
  methods: {
    // Update ARIA attributes based on current state
    updateARIAAttributes() {
      if (!this.$el || typeof this.$el.querySelectorAll !== 'function') {
        return;
      }

      const navLinks = this.$el.querySelectorAll('.enhanced-nav-link');
      navLinks.forEach((link) => {
        const isActive = link.getAttribute('aria-current') === 'page';
        link.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // Ensure active link is focusable
      const activeLink = this.$el.querySelector('[aria-current="page"]');
      if (activeLink) {
        activeLink.setAttribute('tabindex', '0');
      }
    },
    
    // Verificar si el usuario tiene permisos para un módulo
    hasPermission(permission) {
      const permissions = this.user?.permissions || {};
      return permissions[permission] === true;
    },
    
    // Verificar si una ruta está activa
    isActiveRoute(route) {
      if (typeof route === 'string') {
        return this.$route.path === route;
      }
      if (route.name) {
        return this.$route.name === route.name;
      }
      return false;
    },
    
    // Toggle del menú más opciones
    toggleMoreMenu() {
      this.showMoreMenu = !this.showMoreMenu;
    },
    
    // Cerrar menú más opciones
    closeMoreMenu() {
      this.showMoreMenu = false;
    },
    
    // Tracking de navegación (analytics)
    trackNavigation(item) {
      // Analytics tracking
      console.log(`Bottom nav: Usuario navegó a ${item.name}`);
      
      // Anunciar navegación para accesibilidad
      this.announceNavigation(item);
    },
    
    // Método de logout
    async logoutUser() {
      try {
        const confirmLogout = await this.$confirm(
          '¿Estás seguro de que quieres cerrar sesión?',
          'Confirmar cierre de sesión',
          {
            confirmButtonText: 'Sí, cerrar sesión',
            cancelButtonText: 'Cancelar',
            type: 'warning'
          }
        );

        if (confirmLogout) {
          this.closeMoreMenu();
          await this.$store.dispatch("auth/logout");
          this.$router.push("/login");
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$router.push("/login");
        }
      }
    },
    
    // Manejo de clics fuera del componente
    handleOutsideClick(event) {
      const bottomNav = this.$el;
      if (bottomNav && !bottomNav.contains(event.target)) {
        this.closeMoreMenu();
      }
    },
    
    // Manejo de navegación por teclado
    handleKeyNavigation(event) {
      const { key } = event;
      
      if (key === 'Escape' && this.showMoreMenu) {
        this.closeMoreMenu();
        return;
      }
      
      if (key === 'Tab') {
        this.handleTabNavigation(event);
      }
      
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        this.handleArrowNavigation(event);
      }
    },
    
    // Navegación con flechas entre items
    handleArrowNavigation(event) {
      if (!this.$el || typeof this.$el.querySelectorAll !== 'function') {
        return;
      }

      event.preventDefault();
      const { key } = event;
      const currentElement = document.activeElement;
      const navLinks = Array.from(this.$el.querySelectorAll('.enhanced-nav-link'));
      const currentIndex = navLinks.indexOf(currentElement);

      if (currentIndex === -1) return;

      let nextIndex;
      if (key === 'ArrowLeft') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : navLinks.length - 1;
      } else {
        nextIndex = currentIndex < navLinks.length - 1 ? currentIndex + 1 : 0;
      }

      navLinks[nextIndex]?.focus();
    },
    
    // Manejo de navegación con Tab
    handleTabNavigation(event) {
      if (!this.showMoreMenu || !this.$el || typeof this.$el.querySelectorAll !== 'function') {
        return;
      }

      const focusableElements = this.$el.querySelectorAll(
        '.enhanced-menu-item, .close-menu-btn, .logout-btn-bottom'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable?.focus();
        }
      }
    },
    
    // Anunciar cambios para lectores de pantalla
    announceNavigation(item) {
      const announcement = `Navegando a ${item.name}`;
      this.announceToScreenReader(announcement);
    },
    
    // Utilidad para anuncios a lectores de pantalla
    announceToScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.setAttribute('class', 'sr-only');
      announcement.textContent = message;
      
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  },
  
  // Lifecycle hooks
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeyNavigation);
    
    // Set initial ARIA attributes
    this.$nextTick(() => {
      this.updateARIAAttributes();
    });
  },
  
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyNavigation);
  },
  
  // Watch for route changes to update ARIA
  watch: {
    '$route'() {
      this.$nextTick(() => {
        this.updateARIAAttributes();
      });
    },
    
    showMoreMenu(isOpen) {
      this.$nextTick(() => {
        if (isOpen && this.$el && typeof this.$el.querySelector === 'function') {
          // Focus first menu item when opening
          const firstMenuItem = this.$el.querySelector('.enhanced-menu-item');
          firstMenuItem?.focus();
        }
        this.updateARIAAttributes();
      });
    }
  },
}
</script>

<style scoped>
/* Contenedor principal del bottom navigation */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1025;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(23, 173, 55, 0.1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  padding: env(safe-area-inset-bottom) 0 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.bottom-nav-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0.5rem 1rem;
  max-width: 100%;
  margin: 0 auto;
}

/* Items individuales del bottom nav */
.bottom-nav-item {
  flex: 1;
  display: flex;
  justify-content: center;
  max-width: 80px;
  position: relative;
}

.bottom-nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: var(--dm-text-secondary, #6b7280);
  padding: 0.5rem 0.25rem;
  border-radius: 0.75rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 100%;
  min-height: 60px;
}

.bottom-nav-link:hover,
.bottom-nav-link:focus {
  color: var(--dm-accent-primary, #17ad37);
  background: rgba(23, 173, 55, 0.05);
  transform: translateY(-2px);
}

/* Contenedor del icono */
.nav-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.25rem;
}

.nav-icon {
  font-size: 1.25rem;
  transition: all 0.3s ease;
}

.nav-icon.rotate {
  transform: rotate(180deg);
}

/* Badge de notificaciones */
.nav-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  font-size: 0.625rem;
  font-weight: 700;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* Indicador de elemento activo */
.active-indicator {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: var(--dm-accent-primary, #17ad37);
  border-radius: 50%;
  animation: active-glow 2s ease-in-out infinite;
}

@keyframes active-glow {
  0%, 100% { 
    box-shadow: 0 0 4px var(--dm-accent-primary, #17ad37);
  }
  50% { 
    box-shadow: 0 0 8px var(--dm-accent-primary, #17ad37), 0 0 12px var(--dm-accent-primary, #17ad37);
  }
}

/* Label del navegador */
.nav-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Estados activos */
.bottom-nav-item.active .bottom-nav-link {
  color: var(--dm-accent-primary, #17ad37);
  background: rgba(23, 173, 55, 0.1);
}

.bottom-nav-item.active .nav-icon {
  transform: scale(1.1);
}

.bottom-nav-item.active .nav-label {
  font-weight: 700;
}

/* Menú desplegable "Más" */
.more-menu {
  position: absolute;
  bottom: 100%;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(23, 173, 55, 0.15);
  border-radius: 1rem 1rem 0 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
  width: 100vw;
  max-height: 70vh;
  overflow-y: auto;
  margin-bottom: 0.5rem;
  animation: slideUpIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slideUpIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.more-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(23, 173, 55, 0.1);
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.05) 0%, rgba(152, 236, 45, 0.05) 100%);
}

.more-menu-header h6 {
  margin: 0;
  font-weight: 700;
  color: var(--dm-text-primary, #1f2937);
}

.more-menu-header button {
  background: none;
  border: none;
  color: var(--dm-text-secondary, #6b7280);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.more-menu-header button:hover {
  color: var(--dm-accent-primary, #17ad37);
  background: rgba(23, 173, 55, 0.1);
}

/* Grid de módulos en el menú más */
.more-menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
}

.more-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: var(--dm-text-secondary, #6b7280);
  padding: 1rem 0.5rem;
  border-radius: 1rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(23, 173, 55, 0.1);
}

.more-menu-item:hover {
  color: var(--dm-accent-primary, #17ad37);
  background: rgba(23, 173, 55, 0.1);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgba(23, 173, 55, 0.15);
}

.more-item-icon {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.more-menu-item:hover .more-item-icon {
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #49AB26) 100%);
  transform: scale(1.1);
}

.more-menu-item:hover .more-item-icon i {
  color: white;
}

.more-item-icon i {
  font-size: 1.25rem;
  color: var(--dm-accent-primary, #17ad37);
  transition: color 0.3s ease;
}

.more-item-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
}

/* Sección de usuario en el menú más */
.more-menu-user {
  border-top: 1px solid rgba(23, 173, 55, 0.1);
  padding: 1rem 1.5rem;
  background: rgba(248, 250, 252, 0.8);
}

.user-info-bottom {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar-bottom {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #98ec2d) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
}

.user-details-bottom {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--dm-text-primary, #1f2937);
  line-height: 1.2;
}

.user-role {
  font-size: 0.75rem;
  color: var(--dm-text-secondary, #6b7280);
  font-weight: 500;
}

.logout-btn-bottom {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: var(--dm-text-danger, #dc2626);
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logout-btn-bottom:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%);
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.2);
}

/* Overlay para cerrar el menú */
.more-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: -1;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive adjustments */
@media (max-width: 375px) {
  .more-menu-grid {
    grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
    gap: 0.75rem;
    padding: 1rem;
  }
  
  .more-menu-item {
    padding: 0.75rem 0.25rem;
  }
  
  .more-item-icon {
    width: 2rem;
    height: 2rem;
  }
  
  .more-item-label {
    font-size: 0.6875rem;
  }
}

/* Dark mode support */
body.dark-mode-active .bottom-nav {
  background: rgba(17, 24, 39, 0.95);
  border-top-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .more-menu {
  background: rgba(17, 24, 39, 0.98);
  border-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .more-menu-header {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  border-bottom-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .more-menu-user {
  background: rgba(31, 41, 55, 0.8);
  border-top-color: rgba(55, 65, 81, 0.3);
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Enhanced accessibility styles */
.enhanced-nav-link:focus,
.enhanced-menu-item:focus,
.close-menu-btn:focus,
.logout-btn-bottom:focus {
  outline: 3px solid var(--primary-500, #3b82f6);
  outline-offset: 2px;
  border-radius: 8px;
}

/* Screen reader only text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Enhanced ripple effect */
.ripple-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
}

@media (max-width: 991.98px) {
  .enhanced-nav-link {
    min-height: 60px;
    min-width: 60px;
    position: relative;
    overflow: hidden;
  }
  
  .enhanced-nav-link::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(59, 130, 246, 0.2);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
    pointer-events: none;
    z-index: 1;
  }
  
  .enhanced-nav-link:active::before {
    width: 120px;
    height: 120px;
  }
  
  /* Enhanced active state */
  .bottom-nav-item.active .enhanced-nav-link {
    background: rgba(59, 130, 246, 0.1);
  }
  
  .bottom-nav-item.active .enhanced-nav-link::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
    height: 3px;
    background: linear-gradient(90deg, 
      var(--primary-500, #3b82f6) 0%, 
      var(--primary-600, #2563eb) 100%);
    border-radius: 0 0 8px 8px;
    animation: slideDownActive 0.3s ease;
  }
  
  @keyframes slideDownActive {
    from {
      height: 0;
      opacity: 0;
    }
    to {
      height: 3px;
      opacity: 1;
    }
  }
}

/* Enhanced more menu accessibility */
.enhanced-more-menu {
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
}

.enhanced-menu-item {
  min-height: 80px;
  min-width: 80px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.enhanced-menu-item:hover,
.enhanced-menu-item:focus {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
}

.close-menu-btn {
  min-height: 44px;
  min-width: 44px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.close-menu-btn:hover,
.close-menu-btn:focus {
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary-600, #2563eb);
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .enhanced-bottom-nav {
    border-top-width: 3px;
    border-top-color: var(--primary-600, #2563eb);
  }
  
  .enhanced-more-menu {
    border-width: 2px;
    border-color: var(--primary-600, #2563eb);
  }
  
  .enhanced-nav-link,
  .enhanced-menu-item {
    border: 2px solid transparent;
  }
  
  .enhanced-nav-link:focus,
  .enhanced-menu-item:focus {
    border-color: var(--primary-600, #2563eb);
    outline: none;
  }
  
  .bottom-nav-item.active .enhanced-nav-link {
    border-color: var(--primary-600, #2563eb);
    background: rgba(59, 130, 246, 0.2);
  }
}
</style>